// ============================================
// pages/gunny-bags/GunnyUtilizationForm.tsx
// Create/edit a single gunny utilization entry. Used by /gunny-bags/utilization/:id
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, CheckCircle, XCircle, Package, ShieldCheck } from 'lucide-react';
import {
  seasonsAPI, districtsAPI, commoditiesAPI, gunnySuppliersAPI, gunnyUtilizationAPI,
  gunnyPosAPI,
} from '../../api/services';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole, num } from '../../types/markfed';
import type { Season, District } from '../../types/markfed';

const empty = {
  season_id: 0,
  commodity_id: 0,
  district_id: 0,
  supplier_id: 0,
  society_name: '',
  bags_supplied: 0,
  bags_utilized: 0,
  bags_returned: 0,
  debit_note_bags: 0,
  debit_note_number: '',
  debit_note_date: '',
  supplier_invoice_no: '',
  supplier_invoice_date: '',
  eway_bill_no: '',
  bill_submission_date: '',
  bill_raised_bags: 0,
  certified_bags: 0,
  condition_society: '',
  condition_warehouse: '',
  condition_dm: '',
  rate_per_bag_incl_gst: 0,
  net_amount: 0,
  cgst: 0,
  sgst: 0,
  gross_amount: 0,
  payment_released: 0,
  balance_to_release: 0,
};

export const GunnyUtilizationForm: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();

  const isCreate = !idParam || idParam === 'new';
  const recordId = isCreate ? null : parseInt(idParam);

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [form, setForm] = useState<any>(empty);
  const [status, setStatus] = useState<string>('draft');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isLocked = ['submitted', 'dm_verified', 'manager_approved'].includes(status);
  const isDM = user?.role === UserRole.DM;
  const [stockInfo, setStockInfo] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, d, c] = await Promise.all([seasonsAPI.list(), districtsAPI.list(), commoditiesAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setDistricts(d.data);
        setCommodities(c.data);

        const active = list.find((x) => x.is_active) || list[0];

        if (isCreate) {
          setForm((f: any) => ({
            ...f,
            season_id: active?.id || 0,
            district_id: isDM ? user?.district_id || 0 : 0,
          }));
        } else if (recordId && active) {
          // gunnyUtilizationAPI.get expects (seasonId, id) — try active season first
          try {
            const res = await gunnyUtilizationAPI.get(active.id, recordId);
            const raw = res.data as any;
            setForm({ ...empty, ...raw });
            setStatus(raw.status || 'draft');
          } catch (err: any) {
            setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load record' });
          }
        }
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load data' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!form.district_id) { setSuppliers([]); return; }
    (async () => {
      try {
        const res = await gunnySuppliersAPI.list(form.district_id);
        setSuppliers(Array.isArray(res.data) ? res.data : []);
      } catch {
        setSuppliers([]);
      }
    })();
  }, [form.district_id]);

  // Load remaining bag stock from POs when supplier/district changes
  useEffect(() => {
    if (!form.season_id || !form.supplier_id) { setStockInfo(null); return; }
    (async () => {
      try {
        const res = await gunnyPosAPI.remaining(form.season_id, {
          supplier_id: form.supplier_id,
          district_id: form.district_id || undefined,
        });
        const data = Array.isArray(res.data) ? res.data : [];
        const match = data.find((r: any) => r.supplier_id === form.supplier_id);
        setStockInfo(match || null);
      } catch { setStockInfo(null); }
    })();
  }, [form.season_id, form.supplier_id, form.district_id]);

  // Auto compute net / gross from bags x rate
  const computed = useMemo(() => {
    const billable = num(form.certified_bags) || num(form.bags_utilized);
    const net = +(billable * num(form.rate_per_bag_incl_gst)).toFixed(2);
    const cgst = +(net * 0.025).toFixed(2);
    const sgst = +(net * 0.025).toFixed(2);
    const gross = +(net + cgst + sgst).toFixed(2);
    const balance = +(gross - num(form.payment_released)).toFixed(2);
    return { net, cgst, sgst, gross, balance };
  }, [form.certified_bags, form.bags_utilized, form.rate_per_bag_incl_gst, form.payment_released]);

  useEffect(() => {
    setForm((f: any) => ({
      ...f,
      net_amount: computed.net,
      cgst: computed.cgst,
      sgst: computed.sgst,
      gross_amount: computed.gross,
      balance_to_release: Math.max(computed.balance, 0),
    }));
  }, [computed]);

  const handleChange = (field: string, value: any) =>
    setForm((f: any) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.season_id || !form.commodity_id || !form.district_id || !form.supplier_id) {
      setMessage({ type: 'error', text: 'Season, commodity, district and supplier are required' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const seasonId = form.season_id;
      if (isCreate) {
        const res = await gunnyUtilizationAPI.create(seasonId, form);
        const newId = (res.data as any)?.id;
        setMessage({ type: 'success', text: 'Saved' });
        if (newId) navigate(`/gunny-bags/utilization/${newId}`, { replace: true });
      } else if (recordId) {
        await gunnyUtilizationAPI.update(seasonId, recordId, form);
        setMessage({ type: 'success', text: 'Updated' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const handleDmCertify = async () => {
    if (!recordId || !form.season_id) return;
    if (!num(form.certified_bags) || !num(form.rate_per_bag_incl_gst)) {
      setMessage({ type: 'error', text: 'Certified bags and rate are required to certify' });
      return;
    }
    const confirmMsg =
      `Certify ${num(form.certified_bags).toLocaleString()} bags at ₹${num(form.rate_per_bag_incl_gst).toFixed(2)}?\n` +
      `This will flip the row to 'dm_verified' and hand it off to Manager Procurement.`;
    if (!window.confirm(confirmMsg)) return;

    setSaving(true);
    setMessage(null);
    try {
      await gunnyUtilizationAPI.dmCertify(form.season_id, recordId, {
        certified_bags: num(form.certified_bags),
        rate_per_bag_incl_gst: num(form.rate_per_bag_incl_gst),
        condition_warehouse: form.condition_warehouse || undefined,
        condition_dm: form.condition_dm || undefined,
      });
      setMessage({ type: 'success', text: 'Certified — row moved to dm_verified' });
      // reload the record to reflect server-computed totals + new status
      const res = await gunnyUtilizationAPI.get(form.season_id, recordId);
      setForm({ ...empty, ...res.data } as any);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Certify failed' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-amber-600 animate-spin" />
        <span className="ml-2 text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <button
        onClick={() => navigate('/gunny-bags/utilization')}
        className="mb-4 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to List
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Package className="w-7 h-7 text-amber-600" />
          {isCreate ? 'New Gunny Utilization' : 'Edit Gunny Utilization'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">Status: <span className="font-semibold uppercase">{status}</span></p>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Season</label>
            <select
              value={form.season_id} onChange={(e) => handleChange('season_id', parseInt(e.target.value))}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            >
              <option value={0}>-- Select --</option>
              {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Commodity</label>
            <select
              value={form.commodity_id} onChange={(e) => handleChange('commodity_id', parseInt(e.target.value))}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            >
              <option value={0}>-- Select --</option>
              {commodities.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">District</label>
            <select
              value={form.district_id} onChange={(e) => handleChange('district_id', parseInt(e.target.value))}
              disabled={isLocked || isDM}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            >
              <option value={0}>-- Select --</option>
              {districts.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Supplier</label>
            <select
              value={form.supplier_id} onChange={(e) => handleChange('supplier_id', parseInt(e.target.value))}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            >
              <option value={0}>-- Select --</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.supplier_name} {s.tender_id ? `(Tender ${s.tender_id})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Society Name</label>
            <input type="text" value={form.society_name || ''} onChange={(e) => handleChange('society_name', e.target.value)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            />
          </div>
        </div>

        <h3 className="mt-6 mb-3 text-sm font-semibold text-gray-700 border-b pb-2">Bags</h3>

        {/* Dynamic stock remaining from POs */}
        {stockInfo && (
          <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
            <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-2">Stock from Purchase Orders</h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
              <div>
                <p className="text-[10px] text-gray-500">PO Total</p>
                <p className="text-lg font-bold text-gray-800">{Number(stockInfo.po_bags).toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500">Already Issued</p>
                <p className="text-lg font-bold text-blue-600">{Number(stockInfo.supplied).toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500">Utilized</p>
                <p className="text-lg font-bold text-purple-600">{Number(stockInfo.utilized).toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500">Returned</p>
                <p className="text-lg font-bold text-green-600">{Number(stockInfo.returned).toLocaleString('en-IN')}</p>
              </div>
              <div className={`rounded-lg p-1 ${stockInfo.remaining > 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                <p className="text-[10px] text-gray-500">Remaining</p>
                <p className={`text-lg font-bold ${stockInfo.remaining > 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {Number(stockInfo.remaining).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </div>
        )}
        {form.supplier_id > 0 && !stockInfo && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs text-gray-500">
            No approved Purchase Orders found for this supplier. Create and approve a Gunny PO first.
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            ['bags_supplied', 'Bags Supplied'],
            ['bags_utilized', 'Bags Utilized'],
            ['bags_returned', 'Bags Returned'],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
              <input type="number" value={form[k] || ''}
                onChange={(e) => handleChange(k, parseInt(e.target.value) || 0)}
                disabled={isLocked}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50"
              />
            </div>
          ))}
        </div>

        <h3 className="mt-6 mb-3 text-sm font-semibold text-gray-700 border-b pb-2">Condition</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Per Society</label>
            <input type="text" value={form.condition_society || ''}
              onChange={(e) => handleChange('condition_society', e.target.value)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Per Warehouse</label>
            <input type="text" value={form.condition_warehouse || ''}
              onChange={(e) => handleChange('condition_warehouse', e.target.value)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Per DM (tender T&amp;C)</label>
            <input type="text" value={form.condition_dm || ''}
              onChange={(e) => handleChange('condition_dm', e.target.value)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleSave}
            disabled={saving || isLocked}
            className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isCreate ? 'Save Draft' : 'Update'}
          </button>

          {/* DM certify action — only when the row is still editable and we're a DM.
              Clicking this calls PATCH /dm-certify which computes gross/CGST/SGST/net
              server-side and flips the row to 'dm_verified'. */}
          {!isCreate && !isLocked && hasRole(UserRole.DM) && (
            <button
              onClick={handleDmCertify}
              disabled={saving}
              title="Send to Manager Procurement for approval"
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Certify as DM
            </button>
          )}
        </div>
      </div>

    </div>
  );
};

export default GunnyUtilizationForm;
